import PlayCircleFilled from '@mui/icons-material/PlayCircleFilled'
import { useEffect, useRef, useState } from 'react'
import YouTube from 'react-youtube'
import type { YouTubeProps } from 'react-youtube'

interface VideoProps {
  active?: boolean
  dimensions?: {
    width: number
    height: number
  }
  identifier: string
  onClick?: Function
  thumb?: boolean
}

// See YouTube documentation for player parameters:
// https://developers.google.com/youtube/iframe_api_reference?hl=fr
export default function Video({
  active,
  dimensions,
  onClick,
  thumb,
  identifier,
}: VideoProps) {
  const videoRef = useRef<any>(null)
  const className = `video ${active ? ' is-active' : ''} ${
    thumb ? 'c-glide__thumbnail' : 'c-slider__preview'
  }`

  const [player, setPlayer] = useState<any>(null)

  useEffect(() => {
    if (!player) {
      return
    }

    // When using autoplay, all videos are starting at the same time.  This is
    // due to react slick (slider) that loads every block many times (cloned
    // elements).  A workaround is to check if the current component is active
    // using its ref to ensure that only the current video is the one to play
    // (note the cloned ones).
    if (!active) {
      player.stopVideo()
    } else if (videoRef.current?.closest('.slick-active')) {
      player.playVideo()
    }
  }, [active, player])

  if (!thumb) {
    const { width = 0, height = 0 } = dimensions || {}
    const opts: YouTubeProps['opts'] = {
      height,
      width,
      playerVars: {
        controls: 0,
        fs: 0,
        modestbranding: 1,
      },
    }

    return (
      <div className={className} ref={videoRef}>
        <div className='c-slider__video-overlay c-slider__video-overlay-left' />
        <div className='c-slider__video-overlay c-slider__video-overlay-right' />
        <YouTube
          videoId={identifier}
          opts={opts}
          onEnd={restartPlayer}
          onReady={onReady}
          style={{ marginLeft: -(width - height) / 2 }}
        />
      </div>
    )
  }

  return (
    <button className={className} onClick={handleClick}>
      <PlayCircleFilled />
    </button>
  )

  function handleClick() {
    if (thumb) {
      onClick?.()
    }
  }

  function onReady({ target }: { target: any }) {
    setPlayer(target)
  }

  // Loop video.  This should be done using the player "loop" parameter. Sadly
  // this won't work due to parameter incompatibility with embed player.
  function restartPlayer() {
    player.seekTo(0, true)
  }
}
